import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import { Icon, LatLng } from 'leaflet';
import { User } from '../../services/authService';
import { mapsService, POI_CATEGORIES, POIResult, GeocodeResult } from '../../services/mapsService';
import { NotesService, Note } from '../../services/notesService';
import PageHeader from '../common/PageHeader';
import 'leaflet/dist/leaflet.css';
import './Maps.css';

interface MapsProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface MapMarker {
  id: string;
  position: [number, number];
  label?: string;
  address?: string;
  isPlace?: boolean;
  noteId?: string;
}

interface PlaceFormData {
  name: string;
  address: string;
  lat: number;
  lng: number;
}

// Fix for default marker icons in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to handle map clicks with reverse geocoding
function MapClickHandler({ 
  onMapClick, 
  onReverseGeocode,
  onGeocodingStart,
  onGeocodingEnd
}: { 
  onMapClick: (lat: number, lng: number) => void;
  onReverseGeocode: (lat: number, lng: number, address: string) => void;
  onGeocodingStart: () => void;
  onGeocodingEnd: () => void;
}) {
  useMapEvents({
    click: async (e) => {
      const { lat, lng } = e.latlng;
      onMapClick(lat, lng);
      
      // Reverse geocode
      onGeocodingStart();
      try {
        const result = await mapsService.reverseGeocode(lat, lng);
        const address = mapsService.formatAddress(result);
        onReverseGeocode(lat, lng, address);
      } catch (error) {
        console.error('Reverse geocoding failed:', error);
        onReverseGeocode(lat, lng, `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      } finally {
        onGeocodingEnd();
      }
    },
  });

  return null;
}

// Component to control map view programmatically
function MapController({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();
  
  useEffect(() => {
    // Only update if center is valid (not [0, 0] which is the default/initial state)
    if ((center[0] !== 0 || center[1] !== 0) && !isNaN(center[0]) && !isNaN(center[1])) {
      console.log('MapController: Setting view to', center, 'zoom:', zoom);
      map.setView(center, zoom, { animate: true, duration: 0.5 });
    }
  }, [center, zoom, map]);

  return null;
}

const Maps: React.FC<MapsProps> = ({ user, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [markers, setMarkers] = useState<MapMarker[]>([]);
  const [places, setPlaces] = useState<Note[]>([]);
  const [pois, setPois] = useState<POIResult[]>([]);
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0]);
  const [mapZoom, setMapZoom] = useState<number>(2);
  const [selectedPOICategories, setSelectedPOICategories] = useState<string[]>([]);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [poiSearchQuery, setPoiSearchQuery] = useState('');
  const [locationSearchResults, setLocationSearchResults] = useState<GeocodeResult[]>([]);
  const [showLocationSearch, setShowLocationSearch] = useState(false);
  const [showPOISearch, setShowPOISearch] = useState(false);
  const [showOverlays, setShowOverlays] = useState(false);
  const [showPlacesList, setShowPlacesList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clickedAddress, setClickedAddress] = useState<string | null>(null);
  const [searchCenter, setSearchCenter] = useState<[number, number]>([0, 0]);
  const [searchZoom, setSearchZoom] = useState<number>(2);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [reverseGeocoding, setReverseGeocoding] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showPlaceForm, setShowPlaceForm] = useState(false);
  const [placeFormData, setPlaceFormData] = useState<PlaceFormData>({ name: '', address: '', lat: 0, lng: 0 });
  const [editingPlace, setEditingPlace] = useState<Note | null>(null);
  const [placeFormError, setPlaceFormError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  // Helper function to sanitize place name for note_id
  const sanitizeNoteId = useCallback((input: string): string => {
    if (!input || typeof input !== 'string') {
      return '';
    }
    return input
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 100);
  }, []);

  const handleMapClick = useCallback((lat: number, lng: number) => {
    // Marker creation will be handled by reverse geocoding callback
  }, []);

  const handleReverseGeocode = useCallback((lat: number, lng: number, address: string) => {
    setClickedAddress(address);
    setSearchCenter([lat, lng]);
    // Show place creation form instead of auto-creating marker
    setPlaceFormData({
      name: address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      address: address,
      lat: lat,
      lng: lng
    });
    setShowPlaceForm(true);
    setEditingPlace(null);
    setPlaceFormError(null);
  }, []);

  const handleGeocodingStart = useCallback(() => {
    setReverseGeocoding(true);
  }, []);

  const handleGeocodingEnd = useCallback(() => {
    setReverseGeocoding(false);
  }, []);

  const handleDeleteMarker = useCallback((markerId: string) => {
    setMarkers((prev) => prev.filter((marker) => marker.id !== markerId));
  }, []);

  const handleCreatePlace = useCallback(async () => {
    if (!user?.id) return;

    // Validate form data
    if (!placeFormData.name.trim()) {
      setPlaceFormError('Place name is required');
      return;
    }

    if (placeFormData.lat < -90 || placeFormData.lat > 90) {
      setPlaceFormError('Latitude must be between -90 and 90');
      return;
    }

    if (placeFormData.lng < -180 || placeFormData.lng > 180) {
      setPlaceFormError('Longitude must be between -180 and 180');
      return;
    }

    setPlaceFormError(null);
    setLoading(true);

    try {
      const sanitizedNoteId = sanitizeNoteId(placeFormData.name);
      if (!sanitizedNoteId) {
        setPlaceFormError('Invalid place name');
        setLoading(false);
        return;
      }

      const noteData = {
        user_id: user.id,
        note_id: sanitizedNoteId,
        title: placeFormData.name.trim(),
        type: 'place',
        blocks: [],
        properties: {
          lat: placeFormData.lat,
          lng: placeFormData.lng,
          address: placeFormData.address.trim()
        }
      };

      const newNote = await NotesService.createNote(noteData);
      setPlaces((prev) => [...prev, newNote]);
      setShowPlaceForm(false);
      setPlaceFormData({ name: '', address: '', lat: 0, lng: 0 });
      setEditingPlace(null);
    } catch (error: any) {
      console.error('Error creating place:', error);
      setPlaceFormError(error.message || 'Failed to create place');
    } finally {
      setLoading(false);
    }
  }, [user?.id, placeFormData, sanitizeNoteId]);

  const loadPlaces = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await NotesService.getNotesByType(user.id, 'place', { limit: 1000 });
      console.log('Loaded places:', response.data);
      setPlaces(response.data);
    } catch (error) {
      console.error('Error loading places:', error);
    }
  }, [user?.id]);

  const handleUpdatePlace = useCallback(async () => {
    if (!user?.id || !editingPlace) return;

    // Validate form data
    if (!placeFormData.name.trim()) {
      setPlaceFormError('Place name is required');
      return;
    }

    if (placeFormData.lat < -90 || placeFormData.lat > 90) {
      setPlaceFormError('Latitude must be between -90 and 90');
      return;
    }

    if (placeFormData.lng < -180 || placeFormData.lng > 180) {
      setPlaceFormError('Longitude must be between -180 and 180');
      return;
    }

    setPlaceFormError(null);
    setLoading(true);

    try {
      await NotesService.updateNote(editingPlace.note_id, {
        title: placeFormData.name.trim(),
        type: 'place',
        blocks: editingPlace.blocks,
        properties: {
          lat: placeFormData.lat,
          lng: placeFormData.lng,
          address: placeFormData.address.trim()
        }
      });

      // Reload places
      await loadPlaces();
      setShowPlaceForm(false);
      setPlaceFormData({ name: '', address: '', lat: 0, lng: 0 });
      setEditingPlace(null);
    } catch (error: any) {
      console.error('Error updating place:', error);
      setPlaceFormError(error.message || 'Failed to update place');
    } finally {
      setLoading(false);
    }
  }, [user?.id, editingPlace, placeFormData, loadPlaces]);

  const handleDeletePlace = useCallback(async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this place?')) return;

    try {
      await NotesService.deleteNote(noteId);
      setPlaces((prev) => prev.filter((place) => place.note_id !== noteId));
    } catch (error) {
      console.error('Error deleting place:', error);
      alert('Failed to delete place');
    }
  }, []);

  const handleEditPlace = useCallback((place: Note) => {
    const props = place.properties || {};
    setPlaceFormData({
      name: place.title,
      address: props.address || '',
      lat: props.lat || 0,
      lng: props.lng || 0
    });
    setEditingPlace(place);
    setShowPlaceForm(true);
    setPlaceFormError(null);
  }, []);

  // Parse coordinates from input string (format: lat,lng or lat,lng,zoom or lat,lng,zoomz)
  const parseCoordinates = useCallback((input: string): { lat: number; lng: number; zoom?: number } | null => {
    const trimmed = input.trim();
    
    // Check if input ends with 'z' (zoom indicator)
    const hasZoomSuffix = /z$/i.test(trimmed);
    const coordsStr = trimmed.replace(/z$/i, '').trim();
    
    // Split by comma
    const parts = coordsStr.split(',').map(p => p.trim());
    
    if (parts.length >= 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      
      // Validate coordinates
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        // Extract zoom if present (third part or from 'z' suffix)
        let zoom: number | undefined;
        if (parts.length >= 3) {
          zoom = parseFloat(parts[2]);
          if (isNaN(zoom)) zoom = undefined;
        } else if (hasZoomSuffix && parts.length === 2) {
          // If format is lat,lngz, try to extract zoom from the lng part
          // This handles edge cases, but typically zoom should be separate
          zoom = undefined;
        }
        
        return { lat, lng, zoom };
      }
    }
    
    return null;
  }, []);

  const handleLocationSearch = useCallback(async () => {
    if (!locationSearchQuery.trim()) {
      setLocationSearchResults([]);
      return;
    }

    // Check if input is coordinates
    const coords = parseCoordinates(locationSearchQuery);
    if (coords) {
      // Navigate to coordinates
      setMapCenter([coords.lat, coords.lng]);
      setMapZoom(coords.zoom || 15);
      setSearchCenter([coords.lat, coords.lng]);
      setSearchZoom(coords.zoom || 15);
      setLocationSearchResults([]);
      return;
    }

    // Otherwise, do normal location search
    setLoading(true);
    try {
      const results = await mapsService.searchLocation(locationSearchQuery, 10);
      setLocationSearchResults(results);
    } catch (error) {
      console.error('Location search failed:', error);
      setLocationSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, [locationSearchQuery, parseCoordinates]);

  const handleLocationSelect = useCallback((result: GeocodeResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setSearchCenter([lat, lng]);
    setSearchZoom(15);
    setMapCenter([lat, lng]);
    setMapZoom(15);
    setLocationSearchQuery(result.display_name);
    setLocationSearchResults([]);
    setShowLocationSearch(false);
  }, []);

  const handlePOISearch = useCallback(async () => {
    if (!poiSearchQuery.trim() || (searchCenter[0] === 0 && searchCenter[1] === 0)) {
      setPois([]);
      return;
    }

    setLoading(true);
    try {
      const categories = selectedPOICategories.length > 0 
        ? selectedPOICategories 
        : POI_CATEGORIES.map(c => c.id);
      
      const results = await mapsService.searchPOIs(
        searchCenter[0],
        searchCenter[1],
        2000,
        categories
      );
      
      // Filter by search query if provided
      const filtered = poiSearchQuery.trim()
        ? results.filter(poi => 
            poi.tags.name?.toLowerCase().includes(poiSearchQuery.toLowerCase())
          )
        : results;
      
      setPois(filtered);
    } catch (error) {
      console.error('POI search failed:', error);
      setPois([]);
    } finally {
      setLoading(false);
    }
  }, [poiSearchQuery, searchCenter, selectedPOICategories]);

  const handleToggleCategory = useCallback((categoryId: string) => {
    setSelectedPOICategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  const handleClearPOIs = useCallback(() => {
    setPois([]);
    setPoiSearchQuery('');
  }, []);

  const handleGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const lat = latitude;
        const lng = longitude;

        // Update map center
        setMapCenter([lat, lng]);
        setMapZoom(15);
        setSearchCenter([lat, lng]);
        setSearchZoom(15);

        // Reverse geocode to get address
        try {
          const result = await mapsService.reverseGeocode(lat, lng);
          const address = mapsService.formatAddress(result);
          setClickedAddress(address);
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
          setClickedAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        }

        setLocationLoading(false);
      },
      (error) => {
        setLocationLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location access denied. Please enable location permissions.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out.');
            break;
          default:
            setLocationError('An unknown error occurred.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, []);

  useEffect(() => {
    // Auto-search POIs when categories change or center changes
    if (showPOISearch && (searchCenter[0] !== 0 || searchCenter[1] !== 0)) {
      handlePOISearch();
    }
  }, [selectedPOICategories, searchCenter, showPOISearch]);

  // Load places on mount
  useEffect(() => {
    if (user?.id) {
      loadPlaces();
    }
  }, [user?.id, loadPlaces]);

  // Close menu when clicking outside and calculate position
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      
      // Calculate menu position
      if (menuButtonRef.current) {
        const rect = menuButtonRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right
        });
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  if (!user) {
    return (
      <div className="maps-container">
        <div className="maps-auth-message">
          Please sign in to use Maps
        </div>
      </div>
    );
  }

  return (
    <div className="maps-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="maps-title">/maps</h1>}
        rightActions={
          <div className="maps-menu-container" ref={menuRef}>
            <button
              ref={menuButtonRef}
              className="maps-menu-toggle"
              onClick={() => setShowMenu(!showMenu)}
              title="Menu"
            >
              <span className="maps-menu-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
            {showMenu && (
              <div 
                className="maps-menu-dropdown"
                style={{ 
                  top: `${menuPosition.top}px`, 
                  right: `${menuPosition.right}px` 
                }}
              >
                <button
                  className={`maps-menu-item ${showLocationSearch ? 'active' : ''}`}
                  onClick={() => {
                    setShowLocationSearch(!showLocationSearch);
                    setShowPOISearch(false);
                    setShowOverlays(false);
                    setShowMenu(false);
                  }}
                >
                  🔍 Search
                </button>
                <button
                  className={`maps-menu-item ${showPOISearch ? 'active' : ''}`}
                  onClick={() => {
                    setShowPOISearch(!showPOISearch);
                    setShowLocationSearch(false);
                    setShowOverlays(false);
                    setShowMenu(false);
                  }}
                >
                  📍 POIs
                </button>
                <button
                  className={`maps-menu-item ${showOverlays ? 'active' : ''}`}
                  onClick={() => {
                    setShowOverlays(!showOverlays);
                    setShowLocationSearch(false);
                    setShowPOISearch(false);
                    setShowPlacesList(false);
                    setShowMenu(false);
                  }}
                >
                  🗺️ Layers
                </button>
                <button
                  className={`maps-menu-item ${showPlacesList ? 'active' : ''}`}
                  onClick={() => {
                    setShowPlacesList(!showPlacesList);
                    setShowLocationSearch(false);
                    setShowPOISearch(false);
                    setShowOverlays(false);
                    setShowMenu(false);
                  }}
                >
                  📍 Places
                </button>
                <div className="maps-menu-divider"></div>
                <button
                  className="maps-menu-item"
                  onClick={() => {
                    handleGetCurrentLocation();
                    setShowMenu(false);
                  }}
                  disabled={locationLoading}
                >
                  {locationLoading ? (
                    <>
                      <span className="maps-spinner"></span>
                      <span>Locating...</span>
                    </>
                  ) : (
                    '📍 My Location'
                  )}
                </button>
              </div>
            )}
          </div>
        }
      />

      {/* Location Search Panel */}
      {showLocationSearch && (
        <div className="maps-panel">
          <div className="maps-panel-header">
            <span className="maps-panel-title">Location Search</span>
          </div>
          <div className="maps-panel-content">
            <div className="maps-search-box">
              <input
                type="text"
                className="maps-search-input"
                placeholder="Search location or enter coordinates (lat,lng,zoom)"
                value={locationSearchQuery}
                onChange={(e) => setLocationSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLocationSearch()}
              />
              <button
                className="maps-search-btn"
                onClick={handleLocationSearch}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="maps-spinner"></span>
                    <span>Searching...</span>
                  </>
                ) : (
                  'Search'
                )}
              </button>
            </div>
            {locationSearchResults.length > 0 && (
              <div className="maps-search-results">
                {locationSearchResults.map((result) => (
                  <button
                    key={result.place_id}
                    className="maps-result-item"
                    onClick={() => handleLocationSelect(result)}
                  >
                    <div className="maps-result-name">{result.display_name}</div>
                    <div className="maps-result-type">{result.type}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* POI Search Panel */}
      {showPOISearch && (
        <div className="maps-panel">
          <div className="maps-panel-header">
            <span className="maps-panel-title">POI Search</span>
            {pois.length > 0 && (
              <button
                className="maps-clear-btn"
                onClick={handleClearPOIs}
              >
                Clear
              </button>
            )}
          </div>
          <div className="maps-panel-content">
            <div className="maps-search-box">
              <input
                type="text"
                className="maps-search-input"
                placeholder="Search POIs (e.g., Starbucks, Park)..."
                value={poiSearchQuery}
                onChange={(e) => setPoiSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePOISearch()}
              />
              <button
                className="maps-search-btn"
                onClick={handlePOISearch}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="maps-spinner"></span>
                    <span>Searching...</span>
                  </>
                ) : (
                  'Search'
                )}
              </button>
            </div>
            {pois.length > 0 && (
              <div className="maps-poi-count">
                Found {pois.length} POI{pois.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Feature Overlays Panel */}
      {showOverlays && (
        <div className="maps-panel">
          <div className="maps-panel-header">
            <span className="maps-panel-title">Feature Overlays</span>
          </div>
          <div className="maps-panel-content">
            <div className="maps-categories">
              {POI_CATEGORIES.map((category) => (
                <label key={category.id} className="maps-category-item">
                  <input
                    type="checkbox"
                    checked={selectedPOICategories.includes(category.id)}
                    onChange={() => handleToggleCategory(category.id)}
                  />
                  <span>{category.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Places List Panel */}
      {showPlacesList && (
        <div className="maps-panel">
          <div className="maps-panel-header">
            <span className="maps-panel-title">Places ({places.length})</span>
          </div>
          <div className="maps-panel-content">
            {places.length === 0 ? (
              <div className="maps-empty-state">No places saved yet</div>
            ) : (
              <div className="maps-places-list">
                {places.map((place) => {
                  const props = place.properties || {};
                  // Parse coordinates - they might be stored as strings or numbers
                  const lat = typeof props.lat === 'string' ? parseFloat(props.lat) : props.lat;
                  const lng = typeof props.lng === 'string' ? parseFloat(props.lng) : props.lng;
                  
                  return (
                    <div
                      key={place.note_id}
                      className="maps-place-item-wrapper"
                    >
                      <button
                        className="maps-place-item"
                        onClick={() => {
                          console.log(`Clicked on place "${place.title}"`, { lat, lng, props });
                          if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
                            console.log(`Navigating to [${lat}, ${lng}]`);
                            // Set search center first (used by MapController)
                            setSearchCenter([lat, lng]);
                            setSearchZoom(15);
                            // Also set map center as backup
                            setMapCenter([lat, lng]);
                            setMapZoom(15);
                          } else {
                            console.warn(`Cannot navigate to place "${place.title}": invalid coordinates`, { lat, lng, props });
                          }
                        }}
                      >
                        <div className="maps-place-item-content">
                          <div className="maps-place-item-name">{place.title}</div>
                          {props.address && (
                            <div className="maps-place-item-address">{props.address}</div>
                          )}
                        </div>
                      </button>
                      <button
                        className="maps-place-item-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePlace(place.note_id);
                        }}
                        title="Delete place"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Place Creation/Edit Form Modal */}
      {showPlaceForm && (
        <div className="maps-place-form-overlay" onClick={() => {
          setShowPlaceForm(false);
          setPlaceFormData({ name: '', address: '', lat: 0, lng: 0 });
          setEditingPlace(null);
          setPlaceFormError(null);
        }}>
          <div className="maps-place-form" onClick={(e) => e.stopPropagation()}>
            <div className="maps-place-form-header">
              <span className="maps-place-form-title">
                {editingPlace ? 'Edit Place' : 'Create Place'}
              </span>
              <button
                className="maps-place-form-close"
                onClick={() => {
                  setShowPlaceForm(false);
                  setPlaceFormData({ name: '', address: '', lat: 0, lng: 0 });
                  setEditingPlace(null);
                  setPlaceFormError(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="maps-place-form-content">
              {placeFormError && (
                <div className="maps-place-form-error">{placeFormError}</div>
              )}
              <div className="maps-place-form-field">
                <label className="maps-place-form-label">Place Name *</label>
                <input
                  type="text"
                  className="maps-place-form-input"
                  value={placeFormData.name}
                  onChange={(e) => setPlaceFormData({ ...placeFormData, name: e.target.value })}
                  placeholder="Enter place name"
                />
              </div>
              <div className="maps-place-form-field">
                <label className="maps-place-form-label">Address</label>
                <input
                  type="text"
                  className="maps-place-form-input"
                  value={placeFormData.address}
                  onChange={(e) => setPlaceFormData({ ...placeFormData, address: e.target.value })}
                  placeholder="Enter address"
                />
              </div>
              <div className="maps-place-form-row">
                <div className="maps-place-form-field">
                  <label className="maps-place-form-label">Latitude</label>
                  <input
                    type="number"
                    className="maps-place-form-input"
                    value={placeFormData.lat}
                    onChange={(e) => setPlaceFormData({ ...placeFormData, lat: parseFloat(e.target.value) || 0 })}
                    placeholder="Latitude"
                    min="-90"
                    max="90"
                    step="any"
                  />
                </div>
                <div className="maps-place-form-field">
                  <label className="maps-place-form-label">Longitude</label>
                  <input
                    type="number"
                    className="maps-place-form-input"
                    value={placeFormData.lng}
                    onChange={(e) => setPlaceFormData({ ...placeFormData, lng: parseFloat(e.target.value) || 0 })}
                    placeholder="Longitude"
                    min="-180"
                    max="180"
                    step="any"
                  />
                </div>
              </div>
              <div className="maps-place-form-actions">
                <button
                  className="maps-place-form-btn maps-place-form-btn-cancel"
                  onClick={() => {
                    setShowPlaceForm(false);
                    setPlaceFormData({ name: '', address: '', lat: 0, lng: 0 });
                    setEditingPlace(null);
                    setPlaceFormError(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="maps-place-form-btn maps-place-form-btn-save"
                  onClick={editingPlace ? handleUpdatePlace : handleCreatePlace}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="maps-spinner"></span>
                      <span>{editingPlace ? 'Saving...' : 'Creating...'}</span>
                    </>
                  ) : (
                    editingPlace ? 'Save' : 'Create'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location Error Display */}
      {locationError && (
        <div className="maps-error-display">
          <span className="maps-error-text">{locationError}</span>
          <button
            className="maps-close-error"
            onClick={() => setLocationError(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Clicked Address Display */}
      {clickedAddress && (
        <div className="maps-address-display">
          <span className="maps-address-label">Address:</span>
          <span className="maps-address-text">{clickedAddress}</span>
          <button
            className="maps-close-address"
            onClick={() => setClickedAddress(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Loading Overlay for Reverse Geocoding */}
      {reverseGeocoding && (
        <div className="maps-loading-overlay">
          <div className="maps-loading-content">
            <span className="maps-spinner"></span>
            <span>Getting address...</span>
          </div>
        </div>
      )}

      <div className="maps-content">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          className="maps-map"
          scrollWheelZoom={true}
        >
          <MapController center={searchCenter} zoom={searchZoom} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler 
            onMapClick={handleMapClick} 
            onReverseGeocode={handleReverseGeocode}
            onGeocodingStart={handleGeocodingStart}
            onGeocodingEnd={handleGeocodingEnd}
          />
          
          {/* Place Markers */}
          {places.map((place) => {
            const props = place.properties || {};
            // Parse coordinates - they might be stored as strings or numbers
            const lat = typeof props.lat === 'string' ? parseFloat(props.lat) : props.lat;
            const lng = typeof props.lng === 'string' ? parseFloat(props.lng) : props.lng;
            
            // Validate coordinates exist and are valid numbers
            if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
              console.warn(`Place "${place.title}" has invalid coordinates: lat=${props.lat}, lng=${props.lng}`, props);
              return null;
            }
            
            console.log(`Rendering marker for "${place.title}" at [${lat}, ${lng}]`);
            
            return (
              <Marker 
                key={`place-${place.note_id}`} 
                position={[lat, lng]}
                icon={new Icon({
                  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
                  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
                  iconSize: [25, 41],
                  iconAnchor: [12, 41],
                  popupAnchor: [1, -34],
                  shadowSize: [41, 41]
                })}
              >
                <Popup>
                  <div className="maps-popup">
                    <div className="maps-popup-label">{place.title}</div>
                    {props.address && (
                      <div className="maps-popup-address">{props.address}</div>
                    )}
                    <div className="maps-popup-actions">
                      <button
                        className="maps-popup-btn maps-popup-btn-view"
                        onClick={() => navigate(`/notes/${place.note_id}`)}
                      >
                        View Note
                      </button>
                      <button
                        className="maps-popup-btn maps-popup-btn-edit"
                        onClick={() => handleEditPlace(place)}
                      >
                        Edit
                      </button>
                      <button
                        className="maps-popup-btn maps-popup-btn-delete"
                        onClick={() => handleDeletePlace(place.note_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* Custom Markers */}
          {markers.map((marker) => (
            <Marker key={marker.id} position={marker.position}>
              <Popup>
                <div className="maps-popup">
                  <div className="maps-popup-label">{marker.label}</div>
                  {marker.address && (
                    <div className="maps-popup-address">{marker.address}</div>
                  )}
                  <button
                    className="maps-delete-btn"
                    onClick={() => handleDeleteMarker(marker.id)}
                  >
                    Delete
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* POI Markers */}
          {pois.map((poi) => (
            <Marker key={`poi-${poi.id}`} position={[poi.lat, poi.lon]}>
              <Popup>
                <div className="maps-popup">
                  <div className="maps-popup-label">
                    {poi.tags.name || 'Unnamed POI'}
                  </div>
                  {poi.tags.amenity && (
                    <div className="maps-popup-type">Type: {poi.tags.amenity}</div>
                  )}
                  {poi.tags.shop && (
                    <div className="maps-popup-type">Shop: {poi.tags.shop}</div>
                  )}
                  {poi.tags.tourism && (
                    <div className="maps-popup-type">Tourism: {poi.tags.tourism}</div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default Maps;
